import { useEffect } from 'react';
import { useGame } from '../state/useGame';
import { startHeartbeat } from './heartbeat';
import { Sidebar } from './Sidebar';
import { SkillsPage } from './SkillsPage';
import { CombatPage } from './CombatPage';
import { BankPage } from './BankPage';
import { InventoryPage } from './InventoryPage';
import { EquipmentPage } from './EquipmentPage';
import { SettingsPage } from './SettingsPage';
import { ParliamentPage } from './ParliamentPage';
import { QuestsPage } from './QuestsPage';
import { ToastStack } from './ToastStack';
import { OfflineSummaryModal } from './OfflineSummaryModal';
import { AudioEngine } from '../audio/AudioEngine';

const TICK_MS = 1_000;

export function App() {
  const ready = useGame((s) => s.ready);
  const page = useGame((s) => s.page);
  const saveVersion = useGame((s) => s.state.version);
  const tick = useGame((s) => s.tick);
  const bootstrap = useGame((s) => s.bootstrap);

  // Bootstrap on mount: load save, run offline catchup
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // Heartbeat loop: 1Hz tick while visible; on return from a hidden tab the
  // elapsed window is caught up before the ticker restarts (see heartbeat.ts).
  useEffect(() => {
    if (!ready) return;
    return startHeartbeat({
      tickMs: TICK_MS,
      tick,
      catchUp: () => useGame.getState().applyOfflineCatchup(),
    });
  }, [ready, tick]);

  // Autosave on unload
  useEffect(() => {
    const onBeforeUnload = () => {
      void useGame.getState().saveNow();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  if (!ready) {
    return (
      <div className="app" data-app-state="loading">
        <div className="main">
          <p style={{ color: 'var(--ui-text-dim)' }}>Loading save…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app" data-app-state="ready" data-save-version={saveVersion}>
      <Sidebar />
      <div className="main">
        {page === 'skills' && <SkillsPage />}
        {page === 'combat' && <CombatPage />}
        {page === 'inventory' && <InventoryPage />}
        {page === 'equipment' && <EquipmentPage />}
        {page === 'bank' && <BankPage />}
        {page === 'quests' && <QuestsPage />}
        {page === 'settings' && <SettingsPage />}
        {page === 'parliament' && <ParliamentPage />}
      </div>
      <OfflineSummaryModal />
      <ToastStack />
      <AudioEngine />
    </div>
  );
}